import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircleRounded,
  CancelRounded,
  VisibilityRounded,
  HourglassTopRounded,
  FilterListRounded,
  DeleteOutlineRounded,
  ReceiptLongRounded,
  EditNoteRounded,
  FormatListBulletedRounded,
  WorkspacePremiumRounded,
  SearchRounded,
  SchoolRounded,
  LocalOfferRounded,
  CardGiftcardRounded,
  TuneRounded,
} from '@mui/icons-material';
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { tuitionApi } from '../../lib/api/tuition.api';
import { EditTuitionInstallmentPlanDialog } from './components/EditTuitionInstallmentPlanDialog';
import { AdjustPaymentDialog } from './components/AdjustPaymentDialog';
import { formatMoney } from '../../lib/i18n-format';
import { errorText } from '../../lib/error-text';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Maps stored plan-type ids → i18n key suffixes under `tuition.planLabels.*`.
// Stored ids stay stable; the visible label is resolved via t() at render.
const PLAN_LABELS: Record<string, string> = {
  '3-months': '3-months',
  '6-months': '6-months',
  '9-months': '9-months',
  full: 'full',
};

// ─── Verify/Reject Dialog ─────────────────────────────────────────────────

interface VerifyDialogProps {
  open: boolean;
  payment: DocumentData | null;
  tenantId: string;
  onClose: () => void;
  onSubmit: (
    action: 'verify' | 'reject',
    reason?: string,
    correctedAllocations?: Array<{ installmentId: string; amount: number }>,
  ) => Promise<void>;
}

interface VerifyInstallment {
  id: string;
  label: string;
  dueDate: string;
  amount: number;
  type: 'enrollment' | 'monthly';
}

interface VerifyAllocation {
  installmentId: string;
  amount: number;
}

function VerifyDialog({ open, payment, tenantId, onClose, onSubmit }: VerifyDialogProps) {
  const { t } = useTranslation('sis');
  const [action, setAction] = useState<'verify' | 'reject'>('verify');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [installments, setInstallments] = useState<VerifyInstallment[]>([]);
  // Editable correction state, keyed by installmentId: whether the cuota is kept
  // and the amount to verify for it (string for controlled input).
  const [rows, setRows] = useState<Record<string, { included: boolean; amount: string }>>({});

  const declaredAllocations: VerifyAllocation[] = useMemo(
    () => (payment?.allocations as VerifyAllocation[] | undefined) ?? [],
    [payment],
  );

  // Reset transient state and seed editable rows from the declared allocations.
  useEffect(() => {
    if (!open) return;
    setAction('verify');
    setReason('');
    setError(null);
    const seeded: Record<string, { included: boolean; amount: string }> = {};
    for (const a of declaredAllocations) {
      seeded[a.installmentId] = { included: true, amount: String(a.amount) };
    }
    setRows(seeded);
  }, [open, declaredAllocations]);

  // Load tuition plan to resolve installment labels (same pattern as AdjustPaymentDialog).
  useEffect(() => {
    if (!open || !payment?.tuitionPlanId || !tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(
          doc(db, `tenants/${tenantId}/tuitionPlans/${payment.tuitionPlanId}`),
        );
        if (cancelled) return;
        const data = snap.exists() ? (snap.data() as { installments?: VerifyInstallment[] }) : null;
        setInstallments(data?.installments ?? []);
      } catch {
        if (!cancelled) setInstallments([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, payment?.tuitionPlanId, tenantId]);

  const currency: string = payment?.currency ?? 'CLP';
  const fmt = (n: number) => formatMoney(n, currency);

  const labelFor = (installmentId: string): string => {
    const inst = installments.find((i) => i.id === installmentId);
    return inst ? `${inst.label} (${inst.dueDate})` : installmentId;
  };

  const declaredAmount: number = payment?.amount ?? 0;
  const hasEditableAllocations = declaredAllocations.length > 0;

  // Live corrected total from the editable rows.
  const correctedTotal = declaredAllocations.reduce((sum, a) => {
    const row = rows[a.installmentId];
    if (!row?.included) return sum;
    const n = Number(row.amount);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);

  // A row is invalid if included but empty / non-positive / above what was declared.
  const anyRowInvalid = declaredAllocations.some((a) => {
    const row = rows[a.installmentId];
    if (!row?.included) return false;
    const n = Number(row.amount);
    return isNaN(n) || n <= 0 || n > a.amount;
  });

  const correctionChanged = declaredAllocations.some((a) => {
    const row = rows[a.installmentId];
    return !row?.included || Number(row.amount) !== a.amount;
  });

  const verifyBlocked =
    action === 'verify' && hasEditableAllocations && (anyRowInvalid || correctedTotal <= 0);

  const handleSubmit = async () => {
    if (action === 'reject' && !reason.trim()) {
      setError(t('tuition.errors.reasonRequired'));
      return;
    }
    if (verifyBlocked) {
      setError(t('tuition.verifyCorrection.invalid'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let corrected: VerifyAllocation[] | undefined;
      if (action === 'verify' && hasEditableAllocations && correctionChanged) {
        corrected = declaredAllocations
          .filter((a) => rows[a.installmentId]?.included)
          .map((a) => ({
            installmentId: a.installmentId,
            amount: Number(rows[a.installmentId]!.amount),
          }));
      }
      await onSubmit(action, action === 'reject' ? reason.trim() : undefined, corrected);
      setReason('');
      setAction('verify');
    } catch (e: any) {
      setError(e?.message ?? 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!payment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('tuition.verifyPayment')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.amount')}:{' '}
                <strong style={{ color: 'white' }}>{fmt(declaredAmount)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.paymentDate')}:{' '}
                <strong style={{ color: 'white' }}>{payment.paymentDate}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.reference')}:{' '}
                <strong style={{ color: 'white' }}>{payment.reference}</strong>
              </Typography>
              {payment.notes && (
                <Typography variant="body2" color="text.secondary">
                  {t('tuition.notes')}: {payment.notes}
                </Typography>
              )}
            </Stack>
          </Paper>

          {payment.receiptUrl && (
            <Button
              variant="outlined"
              startIcon={<VisibilityRounded />}
              href={payment.receiptUrl}
              target="_blank"
              rel="noopener"
              size="small"
            >
              {t('tuition.viewReceipt')}
            </Button>
          )}

          <FormControl fullWidth>
            <Select
              value={action}
              onChange={(e) => setAction(e.target.value as 'verify' | 'reject')}
            >
              <MenuItem value="verify">{t('tuition.actions.verify')}</MenuItem>
              <MenuItem value="reject">{t('tuition.actions.reject')}</MenuItem>
            </Select>
          </FormControl>

          {action === 'verify' && hasEditableAllocations && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">{t('tuition.verifyCorrection.title')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('tuition.verifyCorrection.help')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                <Stack spacing={1}>
                  {declaredAllocations.map((a) => {
                    const row = rows[a.installmentId] ?? {
                      included: true,
                      amount: String(a.amount),
                    };
                    return (
                      <Stack key={a.installmentId} direction="row" spacing={1} alignItems="center">
                        <Checkbox
                          size="small"
                          checked={row.included}
                          onChange={(e) =>
                            setRows((prev) => ({
                              ...prev,
                              [a.installmentId]: {
                                included: e.target.checked,
                                amount:
                                  e.target.checked && Number(prev[a.installmentId]?.amount) > 0
                                    ? prev[a.installmentId]!.amount
                                    : String(a.amount),
                              },
                            }))
                          }
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {labelFor(a.installmentId)}
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={row.amount}
                          disabled={!row.included}
                          onChange={(e) =>
                            setRows((prev) => ({
                              ...prev,
                              [a.installmentId]: {
                                included: true,
                                amount: e.target.value,
                              },
                            }))
                          }
                          error={
                            row.included &&
                            (Number(row.amount) <= 0 || Number(row.amount) > a.amount)
                          }
                          helperText={
                            row.included && Number(row.amount) > a.amount
                              ? t('tuition.verifyCorrection.maxHint', { max: fmt(a.amount) })
                              : undefined
                          }
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          sx={{ width: 150 }}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('tuition.verifyCorrection.total')}
                </Typography>
                <Typography variant="body2">
                  <strong>{fmt(correctedTotal)}</strong>
                </Typography>
              </Stack>
              {correctionChanged && correctedTotal > 0 && !anyRowInvalid && (
                <Alert severity="info">
                  {t('tuition.verifyCorrection.willVerify', {
                    declared: fmt(declaredAmount),
                    corrected: fmt(correctedTotal),
                  })}
                </Alert>
              )}
            </Stack>
          )}

          {action === 'reject' && (
            <TextField
              label={t('tuition.rejectionReason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              rows={2}
              fullWidth
              required
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="contained"
          color={action === 'reject' ? 'error' : 'success'}
          onClick={handleSubmit}
          disabled={submitting || verifyBlocked}
          startIcon={
            submitting ? (
              <CircularProgress size={16} />
            ) : action === 'verify' ? (
              <CheckCircleRounded />
            ) : (
              <CancelRounded />
            )
          }
        >
          {action === 'verify' ? t('tuition.actions.verify') : t('tuition.actions.reject')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Delete Plan Confirmation Dialog ─────────────────────────────────────────

interface DeletePlanDialogProps {
  open: boolean;
  plan: DocumentData | null;
  studentName: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  onForceConfirm: () => Promise<void>;
}

function DeletePlanDialog({
  open,
  plan,
  studentName,
  onClose,
  onConfirm,
  onForceConfirm,
}: DeletePlanDialogProps) {
  const { t } = useTranslation('sis');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasActivePayments, setHasActivePayments] = useState(false);
  const [forceConfirmed, setForceConfirmed] = useState(false);

  // Reset force-delete state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setError(null);
      setHasActivePayments(false);
      setForceConfirmed(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (hasActivePayments) {
        // Admin exception path
        await onForceConfirm();
      } else {
        await onConfirm();
      }
      onClose();
    } catch (e: any) {
      const msg: string = e?.message ?? t('tuition.deleteDialog.error');
      // Detect blocking payment error so we can show the force-delete option
      if (msg.includes('active payment')) {
        setHasActivePayments(true);
        setError(null);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan) return null;
  const fmt = (n: number) => formatMoney(n ?? 0, plan.currency ?? 'CLP');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>{t('tuition.deleteDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}

          {hasActivePayments ? (
            // ── Exception mode: active payments detected ──
            <>
              <Alert severity="error" icon={false}>
                <strong>{t('tuition.deleteDialog.activePaymentsTitle')}</strong>{' '}
                {t('tuition.deleteDialog.activePaymentsBody')}
              </Alert>
              <Alert severity="warning">
                <strong>{t('tuition.deleteDialog.irreversibleTitle')}</strong>{' '}
                {t('tuition.deleteDialog.irreversibleBody')}
              </Alert>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'error.dark' }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    {t('tuition.deleteDialog.studentLabel')}{' '}
                    <strong style={{ color: 'white' }}>{studentName}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tuition.deleteDialog.planLabel')}{' '}
                    <strong style={{ color: 'white' }}>
                      {t(`tuition.planLabels.${PLAN_LABELS[plan.paymentPlanOption]}`, {
                        defaultValue: plan.paymentPlanOption,
                      })}
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tuition.deleteDialog.totalLabel')}{' '}
                    <strong style={{ color: 'white' }}>{fmt(plan.totalAmount)}</strong>
                  </Typography>
                </Stack>
              </Paper>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceConfirmed}
                    onChange={(e) => setForceConfirmed(e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2" color="error.light">
                    {t('tuition.deleteDialog.forceConsent')}
                  </Typography>
                }
              />
            </>
          ) : (
            // ── Normal mode ──
            <>
              <Alert severity="warning">
                <Trans
                  i18nKey="tuition.deleteDialog.normalWarning"
                  ns="sis"
                  values={{ studentName }}
                  components={{ strong: <strong /> }}
                />
              </Alert>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    {t('tuition.deleteDialog.planLabel')}{' '}
                    <strong style={{ color: 'white' }}>
                      {t(`tuition.planLabels.${PLAN_LABELS[plan.paymentPlanOption]}`, {
                        defaultValue: plan.paymentPlanOption,
                      })}
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tuition.deleteDialog.totalLabel')}{' '}
                    <strong style={{ color: 'white' }}>{fmt(plan.totalAmount)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('tuition.deleteDialog.installmentsLabel')}{' '}
                    <strong style={{ color: 'white' }}>{plan.installments?.length ?? '-'}</strong>
                  </Typography>
                </Stack>
              </Paper>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={submitting || (hasActivePayments && !forceConfirmed)}
          startIcon={submitting ? <CircularProgress size={16} /> : <DeleteOutlineRounded />}
        >
          {hasActivePayments
            ? t('tuition.deleteDialog.confirmForce')
            : t('tuition.deleteDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Exemption Dialog ─────────────────────────────────────────────────────────

interface ExemptionDialogProps {
  open: boolean;
  plan: DocumentData | null;
  studentName: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}

function ExemptionDialog({ open, plan, studentName, onClose, onConfirm }: ExemptionDialogProps) {
  const { t } = useTranslation('sis');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError(t('tuition.exemptionDialog.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (e: any) {
      setError(e?.message ?? t('tuition.exemptionDialog.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan) return null;
  const fmt = (n: number) => formatMoney(n ?? 0, plan.currency ?? 'CLP');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WorkspacePremiumRounded color="warning" />
        {t('tuition.exemptionDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info" icon={false}>
            <Trans
              i18nKey="tuition.exemptionDialog.info"
              ns="sis"
              values={{ studentName }}
              components={{ strong: <strong /> }}
            />
          </Alert>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.exemptionDialog.currentPlan')}{' '}
                <strong style={{ color: 'white' }}>
                  {t(`tuition.planLabels.${PLAN_LABELS[plan.paymentPlanOption]}`, {
                    defaultValue: plan.paymentPlanOption,
                  })}
                  ​
                </strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.exemptionDialog.totalExonerated')}{' '}
                <strong style={{ color: 'white' }}>
                  {fmt(
                    (() => {
                      const insts: Array<{ amount: number }> = Array.isArray(plan.installments)
                        ? plan.installments
                        : [];
                      const s = insts.reduce(
                        (acc, i) => acc + (isFinite(Number(i.amount)) ? Number(i.amount) : 0),
                        0,
                      );
                      return s > 0
                        ? s
                        : isFinite(Number(plan.frozenTotalFee))
                          ? Number(plan.frozenTotalFee)
                          : 0;
                    })(),
                  )}
                </strong>
              </Typography>
            </Stack>
          </Paper>
          <TextField
            label={t('tuition.exemptionDialog.reasonLabel')}
            placeholder={t('tuition.exemptionDialog.reasonPlaceholder')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={2}
            fullWidth
            required
            helperText={t('tuition.auditHelper')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={submitting || !reason.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : <WorkspacePremiumRounded />}
        >
          {t('tuition.exemptionDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Discount Dialog ──────────────────────────────────────────────────────────

interface DiscountDialogProps {
  open: boolean;
  plan: DocumentData | null;
  studentName: string;
  onClose: () => void;
  onConfirm: (amount: number, reason: string) => Promise<void>;
}

function DiscountDialog({ open, plan, studentName, onClose, onConfirm }: DiscountDialogProps) {
  const { t } = useTranslation('sis');
  const [reason, setReason] = useState('');
  const [amountStr, setAmountStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setReason('');
      setAmountStr('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    const amount = Number(amountStr);
    if (isNaN(amount) || amount <= 0) {
      setError(t('tuition.discountDialog.amountInvalid'));
      return;
    }
    if (!reason.trim()) {
      setError(t('tuition.discountDialog.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(amount, reason.trim());
      onClose();
    } catch (e: any) {
      setError(e?.message ?? t('tuition.discountDialog.error'));
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan) return null;
  const fmt = (n: number) => formatMoney(n ?? 0, plan.currency ?? 'CLP');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalOfferRounded color="info" />
        {t('tuition.discountDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info" icon={false}>
            <Trans
              i18nKey="tuition.discountDialog.info"
              ns="sis"
              values={{ studentName }}
              components={{ strong: <strong /> }}
            />
          </Alert>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.discountDialog.currentPlan')}{' '}
                <strong style={{ color: 'white' }}>
                  {t(`tuition.planLabels.${PLAN_LABELS[plan.paymentPlanOption as string]}`, {
                    defaultValue: plan.paymentPlanOption,
                  })}
                </strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.discountDialog.enrollmentFee')}{' '}
                <strong style={{ color: 'white' }}>{fmt(plan.frozenEnrollmentFee)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.discountDialog.initialTuition')}{' '}
                <strong style={{ color: 'white' }}>{fmt(plan.frozenTotalFee)}</strong>
              </Typography>
              {(plan.discountAmount ?? 0) > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('tuition.discountDialog.previousDiscount')}{' '}
                  <strong style={{ color: 'white' }}>{fmt(plan.discountAmount)}</strong>
                </Typography>
              )}
            </Stack>
          </Paper>
          <TextField
            label={t('tuition.discountDialog.amountLabel')}
            placeholder={t('tuition.discountDialog.amountPlaceholder')}
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            type="number"
            fullWidth
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
          <TextField
            label={t('tuition.discountDialog.reasonLabel')}
            placeholder={t('tuition.discountDialog.reasonPlaceholder')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={2}
            fullWidth
            required
            helperText={t('tuition.auditHelper')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="contained"
          color="info"
          onClick={handleSubmit}
          disabled={submitting || !reason.trim() || !amountStr}
          startIcon={submitting ? <CircularProgress size={16} /> : <LocalOfferRounded />}
        >
          {t('tuition.discountDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Admin Scholarship Dialog ────────────────────────────────────────────────

interface AdminScholarshipDialogProps {
  open: boolean;
  plan: DocumentData | null;
  studentName: string;
  onClose: () => void;
  onConfirm: (enrollmentPct: number, tuitionPct: number, reason: string) => Promise<void>;
}

function AdminScholarshipDialog({
  open,
  plan,
  studentName,
  onClose,
  onConfirm,
}: AdminScholarshipDialogProps) {
  const { t } = useTranslation('sis');
  const [enrollmentPct, setEnrollmentPct] = useState('');
  const [tuitionPct, setTuitionPct] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setEnrollmentPct('');
      setTuitionPct('');
      setReason('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    const ep = Number(enrollmentPct);
    const tp = Number(tuitionPct);
    if (!reason.trim()) {
      setError(t('tuition.scholarshipDialog.reasonRequired'));
      return;
    }
    if (isNaN(ep) || ep < 0 || ep > 100) {
      setError(t('tuition.scholarshipDialog.enrollmentPctInvalid'));
      return;
    }
    if (isNaN(tp) || tp < 0 || tp > 100) {
      setError(t('tuition.scholarshipDialog.tuitionPctInvalid'));
      return;
    }
    if (ep === 0 && tp === 0) {
      setError(t('tuition.scholarshipDialog.atLeastOnePct'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(ep, tp, reason.trim());
      onClose();
    } catch (e: any) {
      setError(e?.message ?? t('tuition.scholarshipDialog.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!plan) return null;
  const fmt = (n: number) => formatMoney(n ?? 0, plan.currency ?? 'CLP');

  const ep = Number(enrollmentPct);
  const tp = Number(tuitionPct);
  const enrollmentDiscount =
    !isNaN(ep) && ep > 0 ? ((plan.frozenEnrollmentFee ?? 0) * ep) / 100 : 0;
  const monthlyInstallments: Array<{ amount: number; type?: string }> = Array.isArray(
    plan.installments,
  )
    ? plan.installments.filter((i: any) => i.type !== 'enrollment')
    : [];
  const arancelTotal = monthlyInstallments.reduce(
    (s, i) => s + (isFinite(Number(i.amount)) ? Number(i.amount) : 0),
    0,
  );
  const tuitionDiscount = !isNaN(tp) && tp > 0 ? (arancelTotal * tp) / 100 : 0;
  const totalDiscount = enrollmentDiscount + tuitionDiscount;

  const alreadyGranted = !!plan.adminScholarshipGrant;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CardGiftcardRounded color="secondary" />
        {t('tuition.scholarshipDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}

          {alreadyGranted && (
            <Alert severity="warning" icon={false}>
              {t('tuition.scholarshipDialog.alreadyGranted')}
            </Alert>
          )}

          <Alert severity="info" icon={false}>
            <Trans
              i18nKey="tuition.scholarshipDialog.info"
              ns="sis"
              values={{ studentName }}
              components={{ strong: <strong /> }}
            />
          </Alert>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.scholarshipDialog.planLabel')}{' '}
                <strong style={{ color: 'white' }}>
                  {t(`tuition.planLabels.${PLAN_LABELS[plan.paymentPlanOption as string]}`, {
                    defaultValue: plan.paymentPlanOption,
                  })}
                </strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.scholarshipDialog.enrollmentFee')}{' '}
                <strong style={{ color: 'white' }}>{fmt(plan.frozenEnrollmentFee ?? 0)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('tuition.scholarshipDialog.unpaidTuition')}{' '}
                <strong style={{ color: 'white' }}>{fmt(arancelTotal)}</strong>
              </Typography>
            </Stack>
          </Paper>

          <Stack direction="row" spacing={2}>
            <TextField
              label={t('tuition.scholarshipDialog.enrollmentPctLabel')}
              placeholder="0"
              value={enrollmentPct}
              onChange={(e) => setEnrollmentPct(e.target.value)}
              type="number"
              inputProps={{ min: 0, max: 100 }}
              fullWidth
              helperText={t('tuition.scholarshipDialog.enrollmentPctHelper')}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              disabled={alreadyGranted}
            />
            <TextField
              label={t('tuition.scholarshipDialog.tuitionPctLabel')}
              placeholder="0"
              value={tuitionPct}
              onChange={(e) => setTuitionPct(e.target.value)}
              type="number"
              inputProps={{ min: 0, max: 100 }}
              fullWidth
              helperText={t('tuition.scholarshipDialog.tuitionPctHelper')}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              disabled={alreadyGranted}
            />
          </Stack>

          {totalDiscount > 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 1,
                borderColor: 'secondary.dark',
                bgcolor: 'rgba(156,39,176,0.06)',
              }}
            >
              <Typography variant="body2" color="secondary.light" fontWeight={600}>
                {t('tuition.scholarshipDialog.totalEstimated', { amount: fmt(totalDiscount) })}
              </Typography>
            </Paper>
          )}

          <TextField
            label={t('tuition.scholarshipDialog.reasonLabel')}
            placeholder={t('tuition.scholarshipDialog.reasonPlaceholder')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={2}
            fullWidth
            required
            helperText={t('tuition.scholarshipDialog.auditHelper')}
            disabled={alreadyGranted}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={submitting || alreadyGranted || !reason.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : <CardGiftcardRounded />}
        >
          {t('tuition.scholarshipDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Plans Tab ────────────────────────────────────────────────────────────────

interface PlansTabProps {
  tenantId: string;
  people: DocumentData[];
}

// Maps stored plan-status ids → i18n key suffixes under `tuition.planStatusLabels.*`.
const PLAN_STATUS_LABEL: Record<string, string> = {
  active: 'active',
  completed: 'completed',
  defaulted: 'defaulted',
  withdrawn: 'withdrawn',
};

function PlansTab({ tenantId, people }: PlansTabProps) {
  const { t } = useTranslation('sis');
  const [plans, setPlans] = useState<DocumentData[]>([]);
  const [programs, setPrograms] = useState<Map<string, string>>(new Map());
  const [selectedPlan, setSelectedPlan] = useState<DocumentData | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exemptOpen, setExemptOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [editPlanOpen, setEditPlanOpen] = useState(false);
  const [scholarshipOpen, setScholarshipOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // ── Filters ──────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planTypeFilter, setPlanTypeFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [exemptFilter, setExemptFilter] = useState('all');

  // ── Data ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, `tenants/${tenantId}/tuitionPlans`), orderBy('createdAt', 'desc')),
      (s) => setPlans(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    return () => unsub();
  }, [tenantId]);

  // Fetch programs once to resolve programId → name
  useEffect(() => {
    const unsub = onSnapshot(collection(db, `tenants/${tenantId}/programs`), (s) => {
      const m = new Map<string, string>();
      s.docs.forEach((d) => m.set(d.id, (d.data()['name'] as string | undefined) ?? '—'));
      setPrograms(m);
    });
    return () => unsub();
  }, [tenantId]);

  const getPersonName = (id: string) => {
    const p = people.find((p) => p.id === id);
    return p ? `${p.firstName} ${p.lastName}` : id;
  };

  const getProgramName = (id: string) => programs.get(id) ?? '—';

  // ── Filter options (computed from live data) ──────────────────────────────
  const planTypeOptions = useMemo(() => {
    const types = new Set(plans.map((p) => p.paymentPlanOption as string).filter(Boolean));
    return Array.from(types).sort();
  }, [plans]);

  const programOptions = useMemo(() => {
    const ids = new Set(plans.map((p) => p.programId as string).filter(Boolean));
    return Array.from(ids)
      .map((id) => ({ id, name: programs.get(id) ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [plans, programs]);

  // ── Filtered rows ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return plans.filter((plan) => {
      // Search by student name
      if (q) {
        const name = getPersonName(plan.studentId as string).toLowerCase();
        if (!name.includes(q)) return false;
      }
      if (statusFilter !== 'all' && plan.status !== statusFilter) return false;
      if (planTypeFilter !== 'all' && plan.paymentPlanOption !== planTypeFilter) return false;
      if (programFilter !== 'all' && plan.programId !== programFilter) return false;
      if (exemptFilter === 'exempt' && !plan.exemptionReason) return false;
      if (exemptFilter === 'not_exempt' && plan.exemptionReason) return false;
      return true;
    });
  }, [plans, search, statusFilter, planTypeFilter, programFilter, exemptFilter, people]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!selectedPlan) return;
    setActionError(null);
    await tuitionApi.deleteTuitionPlan({ tenantId, planId: selectedPlan.id });
  };

  const handleForceDelete = async () => {
    if (!selectedPlan) return;
    setActionError(null);
    await tuitionApi.deleteTuitionPlan({ tenantId, planId: selectedPlan.id, forceDelete: true });
  };

  const handleExemption = async (reason: string) => {
    if (!selectedPlan) return;
    setActionError(null);
    try {
      await tuitionApi.applyTuitionExemption({ tenantId, planId: selectedPlan.id, reason });
    } catch (e: any) {
      setActionError(e?.message ?? t('tuition.exemptionDialog.error'));
      throw e;
    }
  };

  const handleDiscount = async (amount: number, reason: string) => {
    if (!selectedPlan) return;
    setActionError(null);
    try {
      await tuitionApi.applyTuitionDiscount({ tenantId, planId: selectedPlan.id, amount, reason });
    } catch (e: any) {
      setActionError(e?.message ?? t('tuition.discountDialog.error'));
      throw e;
    }
  };

  const handleAdminScholarship = async (
    enrollmentPct: number,
    tuitionPct: number,
    reason: string,
  ) => {
    if (!selectedPlan) return;
    setActionError(null);
    try {
      await tuitionApi.applyAdminScholarship({
        tenantId,
        planId: selectedPlan.id,
        enrollmentDiscountPercent: enrollmentPct,
        tuitionDiscountPercent: tuitionPct,
        reason,
      });
    } catch (e: any) {
      setActionError(e?.message ?? t('tuition.scholarshipDialog.errorAdmin'));
      throw e;
    }
  };

  const fmt = (n: number, currency = 'CLP') => formatMoney(n ?? 0, currency);

  const planStatusColor = (s: string) => {
    if (s === 'active') return 'success';
    if (s === 'completed') return 'info';
    if (s === 'defaulted') return 'error';
    return 'default';
  };

  const hasFilters =
    search ||
    statusFilter !== 'all' ||
    planTypeFilter !== 'all' ||
    programFilter !== 'all' ||
    exemptFilter !== 'all';

  return (
    <Box>
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      {/* ── Filter bar ── */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2, borderRadius: 2, background: 'rgba(255,255,255,0.02)' }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          flexWrap="wrap"
        >
          <FilterListRounded color="action" sx={{ display: { xs: 'none', sm: 'block' } }} />

          {/* Search */}
          <TextField
            size="small"
            placeholder={t('tuition.plansFilter.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />

          {/* Status filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">{t('tuition.plansFilter.allStatuses')}</MenuItem>
              <MenuItem value="active">{t('tuition.planStatusLabels.active')}</MenuItem>
              <MenuItem value="completed">{t('tuition.planStatusLabels.completed')}</MenuItem>
              <MenuItem value="defaulted">{t('tuition.planStatusLabels.defaulted')}</MenuItem>
              <MenuItem value="withdrawn">{t('tuition.planStatusLabels.withdrawn')}</MenuItem>
            </Select>
          </FormControl>

          {/* Plan type filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select value={planTypeFilter} onChange={(e) => setPlanTypeFilter(e.target.value)}>
              <MenuItem value="all">{t('tuition.plansFilter.allPlans')}</MenuItem>
              {planTypeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {t(`tuition.planLabels.${PLAN_LABELS[opt]}`, { defaultValue: opt })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Program filter */}
          {programOptions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={programFilter}
                onChange={(e) => setProgramFilter(e.target.value)}
                startAdornment={
                  <SchoolRounded sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                }
              >
                <MenuItem value="all">{t('tuition.plansFilter.allPrograms')}</MenuItem>
                {programOptions.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Exempt filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select value={exemptFilter} onChange={(e) => setExemptFilter(e.target.value)}>
              <MenuItem value="all">{t('all', { ns: 'common' })}</MenuItem>
              <MenuItem value="exempt">{t('tuition.plansFilter.onlyExempt')}</MenuItem>
              <MenuItem value="not_exempt">{t('tuition.plansFilter.notExempt')}</MenuItem>
            </Select>
          </FormControl>

          {/* Result count + clear */}
          <Stack direction="row" alignItems="center" gap={1} ml="auto">
            <Typography variant="body2" color="text.secondary">
              {t('tuition.plansFilter.planCount', { count: filtered.length })}
            </Typography>
            {hasFilters && (
              <Tooltip title={t('tuition.plansFilter.clearFilters')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('all');
                    setPlanTypeFilter('all');
                    setProgramFilter('all');
                    setExemptFilter('all');
                  }}
                >
                  <CancelRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* ── Table ── */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, overflowX: 'auto' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
              <TableCell>{t('tuition.columns.student')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {t('tuition.plansColumns.program')}
              </TableCell>
              <TableCell>{t('tuition.plansColumns.chosenPlan')}</TableCell>
              <TableCell>{t('tuition.plansColumns.total')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                {t('tuition.plansColumns.installments')}
              </TableCell>
              <TableCell>{t('tuition.columns.status')}</TableCell>
              <TableCell align="right">{t('tuition.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {plans.length === 0
                    ? t('tuition.plansEmpty.none')
                    : t('tuition.plansEmpty.noMatch')}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((plan) => (
                <TableRow key={plan.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Typography fontWeight={500} color="white" fontSize={13}>
                        {getPersonName(plan.studentId as string)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary">
                      {getProgramName(plan.programId as string)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {plan.paymentPlanOption
                        ? t(`tuition.planLabels.${PLAN_LABELS[plan.paymentPlanOption as string]}`, {
                            defaultValue: plan.paymentPlanOption,
                          })
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700} fontSize={13}>
                      {fmt(
                        (() => {
                          const insts = (plan.installments as Array<{ amount: number }>) ?? [];
                          const instSum = insts.reduce(
                            (s, i) => s + (isFinite(Number(i.amount)) ? Number(i.amount) : 0),
                            0,
                          );
                          return instSum > 0
                            ? instSum
                            : isFinite(Number(plan.frozenTotalFee))
                              ? Number(plan.frozenTotalFee)
                              : 0;
                        })(),
                        plan.currency as string,
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">
                      {(plan.installments as unknown[])?.length ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.5} alignItems="flex-start">
                      {plan.exemptionReason ? (
                        <Tooltip
                          title={t('tuition.chips.exemptTooltip', {
                            reason: plan.exemptionReason as string,
                          })}
                        >
                          <Chip
                            size="small"
                            label={t('tuition.chips.exempt')}
                            color="warning"
                            icon={<WorkspacePremiumRounded />}
                            variant="filled"
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          size="small"
                          label={t(
                            `tuition.planStatusLabels.${PLAN_STATUS_LABEL[plan.status as string]}`,
                            { defaultValue: plan.status },
                          )}
                          color={planStatusColor(plan.status as string) as any}
                          variant="outlined"
                        />
                      )}

                      {plan.adminScholarshipGrant && (
                        <Tooltip
                          title={
                            (plan.adminScholarshipGrant.source ?? 'manual') !== 'manual'
                              ? t('tuition.chips.becaReflectTooltip')
                              : t('tuition.chips.scholarshipTooltip', {
                                  pct: plan.adminScholarshipGrant.tuitionDiscountPercent,
                                })
                          }
                        >
                          <Chip
                            size="small"
                            label={
                              (plan.adminScholarshipGrant.source ?? 'manual') !== 'manual' &&
                              plan.adminScholarshipGrant.reason
                                ? `${plan.adminScholarshipGrant.reason} — ${plan.adminScholarshipGrant.tuitionDiscountPercent}%`
                                : t('tuition.chips.scholarship', {
                                    pct: plan.adminScholarshipGrant.tuitionDiscountPercent,
                                  })
                            }
                            color="secondary"
                            icon={<CardGiftcardRounded sx={{ fontSize: '14px !important' }} />}
                            variant="filled"
                          />
                        </Tooltip>
                      )}

                      {(plan.discountAmount ?? 0) > 0 && (
                        <Tooltip title={t('tuition.chips.discountTooltip')}>
                          <Chip
                            size="small"
                            label={t('tuition.chips.discount')}
                            color="info"
                            icon={<LocalOfferRounded sx={{ fontSize: '14px !important' }} />}
                            variant="filled"
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {!plan.exemptionReason && (
                      <>
                        <Tooltip title={t('tuition.rowActions.discount')}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => {
                              setSelectedPlan(plan);
                              setDiscountOpen(true);
                            }}
                          >
                            <LocalOfferRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('tuition.rowActions.scholarship')}>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => {
                              setSelectedPlan(plan);
                              setScholarshipOpen(true);
                            }}
                            sx={plan.adminScholarshipGrant ? { opacity: 0.5 } : {}}
                          >
                            <CardGiftcardRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('tuition.rowActions.exemption')}>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => {
                              setSelectedPlan(plan);
                              setExemptOpen(true);
                            }}
                          >
                            <WorkspacePremiumRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('tuition.rowActions.editInstallments')}>
                          <IconButton
                            size="small"
                            sx={{ color: 'text.secondary' }}
                            onClick={() => {
                              setSelectedPlan(plan);
                              setEditPlanOpen(true);
                            }}
                          >
                            <EditNoteRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title={t('tuition.rowActions.deletePlan')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedPlan(plan);
                          setDeleteOpen(true);
                        }}
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DeletePlanDialog
        open={deleteOpen}
        plan={selectedPlan}
        studentName={selectedPlan ? getPersonName(selectedPlan.studentId as string) : ''}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedPlan(null);
        }}
        onConfirm={handleDelete}
        onForceConfirm={handleForceDelete}
      />

      <ExemptionDialog
        open={exemptOpen}
        plan={selectedPlan}
        studentName={selectedPlan ? getPersonName(selectedPlan.studentId as string) : ''}
        onClose={() => {
          setExemptOpen(false);
          setSelectedPlan(null);
        }}
        onConfirm={handleExemption}
      />

      <DiscountDialog
        open={discountOpen}
        plan={selectedPlan}
        studentName={selectedPlan ? getPersonName(selectedPlan.studentId as string) : ''}
        onClose={() => {
          setDiscountOpen(false);
          setSelectedPlan(null);
        }}
        onConfirm={handleDiscount}
      />

      <EditTuitionInstallmentPlanDialog
        open={editPlanOpen}
        plan={selectedPlan}
        onClose={() => {
          setEditPlanOpen(false);
          setSelectedPlan(null);
        }}
        onConfirm={async (installments, isAdminOverride) => {
          if (!selectedPlan?.id) return;
          try {
            await tuitionApi.updateTuitionPlanInstallments({
              tenantId,
              planId: selectedPlan.id,
              installments,
              isAdminOverride,
            });
          } catch (e: any) {
            setActionError(e.message || t('tuition.editPlanError'));
            throw e;
          }
        }}
      />

      <AdminScholarshipDialog
        open={scholarshipOpen}
        plan={selectedPlan}
        studentName={selectedPlan ? getPersonName(selectedPlan.studentId as string) : ''}
        onClose={() => {
          setScholarshipOpen(false);
          setSelectedPlan(null);
        }}
        onConfirm={handleAdminScholarship}
      />
    </Box>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export function TuitionAdminPage() {
  const { t } = useTranslation(['sis', 'common']);
  const { currentTenantId } = useTenantStore();

  const [activeTab, setActiveTab] = useState(0);
  const [payments, setPayments] = useState<DocumentData[]>([]);
  const [people, setPeople] = useState<DocumentData[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('pending_verification');
  const [selectedPayment, setSelectedPayment] = useState<DocumentData | null>(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Deletion state
  const [deletePaymentId, setDeletePaymentId] = useState<string | null>(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Adjustment state
  const [adjustPayment, setAdjustPayment] = useState<DocumentData | null>(null);

  useEffect(() => {
    if (!currentTenantId) return;

    const unsubPeople = onSnapshot(
      query(collection(db, `tenants/${currentTenantId}/people`), where('deletedAt', '==', null)),
      (s) => setPeople(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      // Sin nombres, los pagos se ven con ids: se dice por qué.
      (e) => setActionError(errorText(e)),
    );

    return () => {
      unsubPeople();
    };
  }, [currentTenantId]);

  useEffect(() => {
    if (!currentTenantId) return;

    const q =
      statusFilter === 'all'
        ? query(
            collection(db, `tenants/${currentTenantId}/tuitionPayments`),
            orderBy('createdAt', 'desc'),
          )
        : query(
            collection(db, `tenants/${currentTenantId}/tuitionPayments`),
            where('status', '==', statusFilter),
            orderBy('createdAt', 'desc'),
          );

    const unsub = onSnapshot(q, (s) => setPayments(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [currentTenantId, statusFilter]);

  const getPersonName = (id: string) => {
    const p = people.find((p) => p.id === id);
    return p ? `${p.firstName} ${p.lastName}` : id;
  };

  const handleVerify = async (
    action: 'verify' | 'reject',
    reason?: string,
    correctedAllocations?: Array<{ installmentId: string; amount: number }>,
  ) => {
    if (!currentTenantId || !selectedPayment) return;
    setActionError(null);
    try {
      await tuitionApi.verifyPayment({
        tenantId: currentTenantId,
        paymentId: selectedPayment.id,
        action,
        ...(reason ? { rejectionReason: reason } : {}),
        ...(correctedAllocations ? { correctedAllocations } : {}),
      });
      setVerifyOpen(false);
      setSelectedPayment(null);
    } catch (e: any) {
      throw e;
    }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentId || !currentTenantId) return;
    const payment = payments.find((p) => p.id === deletePaymentId);
    if (!payment) return;
    setDeletingPayment(true);
    setDeleteError(null);
    try {
      await tuitionApi.deletePayment({
        tenantId: currentTenantId,
        paymentId: deletePaymentId,
        studentId: payment.studentId,
        isAdminOverride: true,
      });
      setDeletePaymentId(null);
    } catch (e: any) {
      setDeleteError(e?.message ?? t('tuition.deletePayment.error'));
    } finally {
      setDeletingPayment(false);
    }
  };

  const fmt = (n: number, currency = 'CLP') => formatMoney(n, currency);

  const statusColor = (s: string) => {
    if (s === 'verified') return 'success';
    if (s === 'pending_verification') return 'warning';
    if (s === 'rejected') return 'error';
    return 'default';
  };

  if (!currentTenantId) return null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="white">
            {t('tuition.adminTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tuition.adminSubtitle')}
          </Typography>
        </Box>
      </Stack>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<ReceiptLongRounded />}
          iconPosition="start"
          label={t('tuition.tabs.payments')}
        />
        <Tab icon={<EditNoteRounded />} iconPosition="start" label={t('tuition.tabs.plans')} />
      </Tabs>

      {/* Tab 0 — Payments */}
      {activeTab === 0 && (
        <>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}

          {/* Filter */}
          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 3, borderRadius: 2, background: 'rgba(255,255,255,0.02)' }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
              flexWrap="wrap"
            >
              <FilterListRounded color="action" />
              <FormControl sx={{ minWidth: 260 }}>
                {/* Map of status → icon component for renderValue */}
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  size="small"
                  renderValue={(val) => {
                    const iconSx = { fontSize: 16, mr: 1 };
                    const icons: Record<string, React.ReactNode> = {
                      pending_verification: (
                        <HourglassTopRounded sx={{ ...iconSx, color: 'warning.main' }} />
                      ),
                      verified: <CheckCircleRounded sx={{ ...iconSx, color: 'success.main' }} />,
                      rejected: <CancelRounded sx={{ ...iconSx, color: 'error.main' }} />,
                      all: <FormatListBulletedRounded sx={iconSx} />,
                    };
                    const labels: Record<string, string> = {
                      pending_verification: t('tuition.paymentStatus.pending_verification'),
                      verified: t('tuition.paymentStatus.verified'),
                      rejected: t('tuition.paymentStatus.rejected'),
                      all: t('all', { ns: 'common' }),
                    };
                    return (
                      <Stack direction="row" alignItems="center">
                        {icons[val as string]}
                        <Typography variant="body2">{labels[val as string] ?? val}</Typography>
                      </Stack>
                    );
                  }}
                >
                  <MenuItem value="pending_verification">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <HourglassTopRounded fontSize="small" sx={{ color: 'warning.main' }} />
                      <span>{t('tuition.paymentStatus.pending_verification')}</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="verified">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CheckCircleRounded fontSize="small" sx={{ color: 'success.main' }} />
                      <span>{t('tuition.paymentStatus.verified')}</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="rejected">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CancelRounded fontSize="small" sx={{ color: 'error.main' }} />
                      <span>{t('tuition.paymentStatus.rejected')}</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="all">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FormatListBulletedRounded fontSize="small" />
                      <span>{t('all', { ns: 'common' })}</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                {payments.length} {t('tuition.payments')}
              </Typography>
            </Stack>
          </Paper>

          {/* Payments Table */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2, overflowX: 'auto' }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
                  <TableCell>{t('tuition.columns.student')}</TableCell>
                  <TableCell>{t('tuition.columns.amount')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {t('tuition.columns.date')}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {t('tuition.columns.reference')}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {t('tuition.columns.method')}
                  </TableCell>
                  <TableCell>{t('tuition.columns.status')}</TableCell>
                  <TableCell align="right">{t('tuition.columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {t('tuition.noPayments')}
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id} hover>
                      <TableCell>
                        <Typography fontWeight={500} color="white">
                          {getPersonName(payment.studentId)}
                        </Typography>
                        {payment.notes && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              fontStyle: 'italic',
                              maxWidth: 220,
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.4,
                            }}
                          >
                            💬 {payment.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={700}>
                          {fmt(payment.amount, payment.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">{payment.paymentDate}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {payment.reference}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">
                          {String(t(`tuition.methods.${payment.method}`, payment.method))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={String(
                            t(`tuition.paymentStatus.${payment.status}`, payment.status),
                          )}
                          color={statusColor(payment.status) as any}
                          variant="outlined"
                          {...(payment.status === 'pending_verification'
                            ? { icon: <HourglassTopRounded /> }
                            : {})}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {payment.receiptUrl && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              startIcon={<VisibilityRounded />}
                              href={payment.receiptUrl}
                              target="_blank"
                              rel="noopener"
                              sx={{ minWidth: 110, borderRadius: 2, fontSize: '0.75rem' }}
                            >
                              {String(t('tuition.viewReceipt'))}
                            </Button>
                          )}
                          {payment.status === 'pending_verification' && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              startIcon={<CheckCircleRounded />}
                              onClick={() => {
                                setSelectedPayment(payment);
                                setVerifyOpen(true);
                              }}
                              sx={{ minWidth: 110, borderRadius: 2, fontSize: '0.75rem' }}
                            >
                              {String(t('tuition.verifyPayment'))}
                            </Button>
                          )}
                          {payment.status === 'verified' && (
                            <Tooltip title={t('tuition.paymentActions.adjust')}>
                              <IconButton
                                size="small"
                                onClick={() => setAdjustPayment(payment)}
                                sx={{
                                  color: 'warning.main',
                                  opacity: 0.7,
                                  ':hover': { opacity: 1 },
                                }}
                              >
                                <TuneRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={t('tuition.paymentActions.delete')}>
                            <IconButton
                              size="small"
                              onClick={() => setDeletePaymentId(payment.id)}
                              sx={{ color: 'error.main', opacity: 0.7, ':hover': { opacity: 1 } }}
                            >
                              <DeleteOutlineRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <VerifyDialog
            open={verifyOpen}
            payment={selectedPayment}
            tenantId={currentTenantId ?? ''}
            onClose={() => {
              setVerifyOpen(false);
              setSelectedPayment(null);
            }}
            onSubmit={handleVerify}
          />

          <AdjustPaymentDialog
            open={!!adjustPayment}
            payment={adjustPayment}
            tenantId={currentTenantId ?? ''}
            studentName={adjustPayment ? getPersonName(adjustPayment.studentId) : ''}
            onClose={() => setAdjustPayment(null)}
            onSuccess={() => setAdjustPayment(null)}
          />

          {/* Delete Payment Dialog */}
          <Dialog
            open={!!deletePaymentId}
            onClose={() => {
              if (!deletingPayment) setDeletePaymentId(null);
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>{t('tuition.deletePayment.title')}</DialogTitle>
            <DialogContent>
              {deleteError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {deleteError}
                </Alert>
              )}
              <Typography color="text.secondary">{t('tuition.deletePayment.body')}</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeletePaymentId(null)}
                color="inherit"
                disabled={deletingPayment}
              >
                {t('actions.cancel', { ns: 'common' })}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeletePayment}
                disabled={deletingPayment}
                startIcon={
                  deletingPayment ? <CircularProgress size={16} /> : <DeleteOutlineRounded />
                }
              >
                {t('tuition.deletePayment.confirm')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Tab 1 — Plans */}
      {activeTab === 1 && <PlansTab tenantId={currentTenantId} people={people} />}
    </Box>
  );
}
